import propTypes from 'prop-types';

/**
 * Helper function to format a message using intl context.
 * This can be used in class components or functional components with contextTypes.
 *
 * @param {Object} context - React context object
 * @param {string|Object} id - Message ID or object with {id, defaultMessage, values}
 * @param {string} [defaultMessage] - Fallback message if translation not found
 * @param {Object} [values] - Values for message formatting
 * @returns {string} Formatted message
 */
export const formatMessage = (context, id, defaultMessage, values) => {
	const intl = context?.intl;

	if (!intl) {
		// Fallback if context is unavailable
		if (typeof id === 'object' && id.id) {
			return id.defaultMessage || id.id;
		}
		return defaultMessage || id || '';
	}

	// Support both object format {id, defaultMessage, values} and separate props
	if (typeof id === 'object') {
		return intl.formatMessage(id, values);
	}

	return intl.formatMessage({id, defaultMessage}, values);
};

/**
 * Simple translation component that formats messages using intl context.
 * This component accesses intl via contextTypes to avoid breaking ref forwarding
 * in forwardRef components (unlike injectIntl HOC).
 *
 * @param {string|Object} id - Message ID or object with {id, defaultMessage, values}
 * @param {string} [defaultMessage] - Fallback message if translation not found
 * @param {Object} [values] - Values for message formatting
 */
const T = ({id, defaultMessage, values}, context) => {
	return formatMessage(context, id, defaultMessage, values);
};

T.contextTypes = {
	intl: propTypes.object,
};

T.propTypes = {
	id: propTypes.oneOfType([propTypes.string, propTypes.object]).isRequired,
	defaultMessage: propTypes.string,
	values: propTypes.object,
};

export default T;
